// ============================================================
// Zone & Wave Generation System (v2.0)
// ============================================================
import { ENEMY_HP_PER_LEVEL, ENEMY_HP_PER_LEVEL_EARLY, ENEMY_HP_EARLY_CAP, ENEMY_ATK_PER_LEVEL, ENEMY_DEF_PER_LEVEL, ENEMY_GOLD_PER_LEVEL, ENEMY_XP_PER_LEVEL, ELITE_HP_MULT, ELITE_ATK_MULT, BOSS_HP_MULT, BOSS_ATK_MULT, BOSS_DEF_MULT, BOSS_GOLD_MULT, BOSS_XP_MULT, BOSS_LOOT_CHANCE, WAVES_PER_ZONE, LEVELS_PER_ZONE, EARLY_GAME_MOVE_SPEED_PENALTY, EARLY_GAME_ATK_SPEED_PENALTY, EARLY_GAME_MAX_LEVEL, MAX_SPEED_LEVEL, } from '../constants/balance.js';
// ---------- Base Enemy Definitions (v5.0 with expanded roster per zone) ----------
export const BASE_ENEMIES = [
    // ── Beast faction — Dark Forest (fast animals) ──
    {
        id: 'wolf', name: 'Dire Wolf', faction: 'beast', tier: 'minion',
        hp: 40, attack: 12, defense: 4, speed: 1.5, attackRange: 'melee',
        xpReward: 15, goldReward: 8, lootChance: 0.08, lootTier: 1,
        spriteKey: 'enemy_wolf', abilities: [],
    },
    {
        id: 'spider', name: 'Feral Wolf', faction: 'beast', tier: 'minion',
        hp: 30, attack: 14, defense: 3, speed: 1.6, attackRange: 'melee',
        xpReward: 13, goldReward: 7, lootChance: 0.07, lootTier: 1,
        spriteKey: 'enemy_wolf', abilities: [],
    },
    {
        id: 'boar', name: 'Black Wolf', faction: 'beast', tier: 'minion',
        hp: 50, attack: 10, defense: 6, speed: 1.2, attackRange: 'melee',
        xpReward: 14, goldReward: 8, lootChance: 0.08, lootTier: 1,
        spriteKey: 'enemy_wolf', abilities: [],
    },
    {
        // Renamed Cave Bear → Pack Stalker. The sprite is `enemy_wolf` (no
        // bear art exists), so the legacy name was a mismatch — "Lieutenant
        // Cave Bear" reading on a wolf body was breaking the player's read.
        // ID kept as 'bear' to avoid save migration churn; only the display
        // name changed.
        id: 'bear', name: 'Pack Stalker', faction: 'beast', tier: 'elite',
        hp: 80, attack: 18, defense: 10, speed: 1.0, attackRange: 'melee',
        xpReward: 30, goldReward: 15, lootChance: 0.15, lootTier: 2,
        spriteKey: 'enemy_wolf', abilities: [],
    },
    {
        id: 'alpha_wolf', name: 'Wolf Lord', faction: 'beast', tier: 'elite',
        hp: 70, attack: 20, defense: 7, speed: 1.7, attackRange: 'melee',
        xpReward: 28, goldReward: 14, lootChance: 0.14, lootTier: 2,
        spriteKey: 'enemy_wolf', abilities: [],
    },
    // ── Undead faction — Haunted Crypts ──
    {
        id: 'skeleton', name: 'Skeleton Warrior', faction: 'undead', tier: 'minion',
        hp: 35, attack: 10, defense: 8, speed: 0.9, attackRange: 'melee',
        xpReward: 12, goldReward: 6, lootChance: 0.10, lootTier: 1,
        spriteKey: 'enemy_skeleton', abilities: [],
    },
    {
        id: 'ghoul', name: 'Skeleton Archer', faction: 'undead', tier: 'minion',
        hp: 45, attack: 13, defense: 5, speed: 1.1, attackRange: 'melee',
        xpReward: 14, goldReward: 7, lootChance: 0.09, lootTier: 1,
        spriteKey: 'enemy_skeleton', abilities: [],
    },
    {
        id: 'zombie', name: 'Skeleton Spearman', faction: 'undead', tier: 'minion',
        hp: 55, attack: 8, defense: 10, speed: 0.6, attackRange: 'melee',
        xpReward: 13, goldReward: 7, lootChance: 0.10, lootTier: 1,
        spriteKey: 'enemy_skeleton', abilities: [],
    },
    {
        id: 'wraith', name: 'Wraith', faction: 'undead', tier: 'elite',
        hp: 60, attack: 22, defense: 3, speed: 1.2, attackRange: 'ranged',
        xpReward: 35, goldReward: 18, lootChance: 0.18, lootTier: 2,
        spriteKey: 'enemy_wraith', abilities: [],
    },
    {
        id: 'lich', name: 'Wraith Elder', faction: 'undead', tier: 'elite',
        hp: 55, attack: 25, defense: 4, speed: 0.8, attackRange: 'ranged',
        xpReward: 38, goldReward: 20, lootChance: 0.20, lootTier: 2,
        spriteKey: 'enemy_wraith', abilities: [],
    },
    // ── Orc faction — Orcish Wastes (slower, heavy) ──
    {
        id: 'orc_grunt', name: 'Orc Warrior', faction: 'orc', tier: 'minion',
        hp: 55, attack: 14, defense: 6, speed: 0.7, attackRange: 'melee',
        xpReward: 18, goldReward: 10, lootChance: 0.10, lootTier: 1,
        spriteKey: 'enemy_orc', abilities: [],
    },
    {
        id: 'goblin', name: 'Orc Scout', faction: 'orc', tier: 'minion',
        hp: 28, attack: 11, defense: 3, speed: 1.4, attackRange: 'ranged',
        xpReward: 15, goldReward: 9, lootChance: 0.09, lootTier: 1,
        spriteKey: 'enemy_orc', abilities: [],
    },
    {
        id: 'troll', name: 'Orc Brute', faction: 'orc', tier: 'minion',
        hp: 75, attack: 16, defense: 8, speed: 0.5, attackRange: 'melee',
        xpReward: 20, goldReward: 12, lootChance: 0.11, lootTier: 1,
        spriteKey: 'enemy_orc', abilities: [],
    },
    {
        id: 'orc_warlord', name: 'Orc Champion', faction: 'orc', tier: 'elite',
        hp: 100, attack: 20, defense: 14, speed: 0.6, attackRange: 'melee',
        xpReward: 40, goldReward: 22, lootChance: 0.20, lootTier: 2,
        spriteKey: 'enemy_orc_elite', abilities: [],
    },
    {
        id: 'orc_shaman', name: 'Orc Elder', faction: 'orc', tier: 'elite',
        hp: 65, attack: 18, defense: 5, speed: 0.8, attackRange: 'ranged',
        xpReward: 35, goldReward: 20, lootChance: 0.18, lootTier: 2,
        spriteKey: 'enemy_orc_elite', abilities: [],
    },
    // ── Demon faction — Demon Rift ──
    {
        id: 'imp', name: 'Hellhound Imp', faction: 'demon', tier: 'minion',
        hp: 30, attack: 16, defense: 2, speed: 1.3, attackRange: 'ranged',
        xpReward: 14, goldReward: 7, lootChance: 0.08, lootTier: 1,
        spriteKey: 'enemy_imp', abilities: [],
    },
    {
        id: 'hellhound', name: 'Hellhound', faction: 'demon', tier: 'minion',
        hp: 50, attack: 18, defense: 5, speed: 1.5, attackRange: 'melee',
        xpReward: 16, goldReward: 9, lootChance: 0.09, lootTier: 1,
        spriteKey: 'enemy_imp', abilities: [],
    },
    {
        id: 'succubus', name: 'Demon Soldier', faction: 'demon', tier: 'elite',
        hp: 75, attack: 24, defense: 6, speed: 1.1, attackRange: 'ranged',
        xpReward: 45, goldReward: 25, lootChance: 0.20, lootTier: 2,
        spriteKey: 'enemy_demon', abilities: [],
    },
    {
        id: 'demon_knight', name: 'Demon Lord', faction: 'demon', tier: 'elite',
        hp: 120, attack: 25, defense: 15, speed: 0.7, attackRange: 'melee',
        xpReward: 50, goldReward: 30, lootChance: 0.22, lootTier: 3,
        spriteKey: 'enemy_demon', abilities: [],
    },
    // ── Goblin faction — Horde of Goblins special event (NOT in normal zone pools) ──
    // Intentionally never listed in any biome's normal wave pool — these are only
    // spawned by the Horde of Goblins event on milestone zone-clear cards. The
    // horde spawns ~50 mooks + ~10 elites bidirectionally and moves 2× base speed;
    // horde-elites take an additional ×2 HP/ATK on top of the global elite mult.
    {
        id: 'goblin_mook_a', name: 'Horde Goblin', faction: 'goblin', tier: 'minion',
        hp: 32, attack: 11, defense: 3, speed: 0.9, attackRange: 'melee',
        xpReward: 12, goldReward: 6, lootChance: 0.05, lootTier: 1,
        spriteKey: 'enemy_goblin_a', abilities: [],
    },
    {
        id: 'goblin_mook_b', name: 'Feral Goblin', faction: 'goblin', tier: 'minion',
        hp: 36, attack: 12, defense: 4, speed: 0.95, attackRange: 'melee',
        xpReward: 13, goldReward: 7, lootChance: 0.06, lootTier: 1,
        spriteKey: 'enemy_goblin_b', abilities: [],
    },
    {
        id: 'goblin_elite', name: 'Goblin Warlord', faction: 'goblin', tier: 'elite',
        hp: 80, attack: 20, defense: 10, speed: 0.85, attackRange: 'melee',
        xpReward: 38, goldReward: 22, lootChance: 0.16, lootTier: 2,
        spriteKey: 'enemy_goblin_elite', abilities: [],
    },
    // ── Dragon faction — Demon Rift boss tier ──
    {
        id: 'dragon', name: 'Dragon Lord', faction: 'dragon', tier: 'boss',
        hp: 200, attack: 30, defense: 18, speed: 0.6, attackRange: 'ranged',
        xpReward: 100, goldReward: 50, lootChance: 1.0, lootTier: 4,
        spriteKey: 'enemy_dragon', abilities: [],
    },
    // ── Zone Boss Definitions (dedicated per-biome final bosses) ──
    {
        id: 'boss_demon1', name: 'Abyssal Demon', faction: 'demon', tier: 'boss',
        hp: 500, attack: 45, defense: 22, speed: 0.8, attackRange: 'melee',
        xpReward: 250, goldReward: 120, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_demon1', abilities: [],
    },
    {
        id: 'boss_demon2', name: 'Demon Overlord', faction: 'demon', tier: 'boss',
        hp: 480, attack: 50, defense: 18, speed: 0.9, attackRange: 'ranged',
        xpReward: 250, goldReward: 120, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_demon2', abilities: [],
    },
    {
        // Bone Warlord (zone 2 Haunted Crypts). Nerfed from the launch numbers
        // (hp 420 / atk 38 / def 28) to hp 320 / atk 32 / def 20 — the ~2× jump
        // across every stat from White Claw (zone 1) made stages 3-4 a wall for
        // Lv5-8 heroes. Still a clear step up from the Forest boss, just not a
        // brick wall: ~76 % HP, ~84 % ATK, ~71 % DEF relative to the old numbers.
        id: 'boss_spearman', name: 'Bone Warlord', faction: 'undead', tier: 'boss',
        hp: 320, attack: 32, defense: 20, speed: 0.7, attackRange: 'melee',
        xpReward: 220, goldReward: 110, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_spearman', abilities: [],
    },
    // Lich boss — locked behind the "Archive Beneath" story quest. Ranged caster so he plays
    // differently from the Bone Warlord: lower HP, higher atk, ranged attacks.
    // Same tuning pass as Bone Warlord above: hp 380→290, atk 36→28, def 18→14.
    {
        id: 'boss_skeleton_mage', name: 'The Archivist, Bound in Bone', faction: 'undead', tier: 'boss',
        hp: 290, attack: 28, defense: 14, speed: 0.55, attackRange: 'ranged',
        xpReward: 260, goldReward: 130, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_skeleton_mage', abilities: [],
    },
    {
        id: 'boss_darkelf', name: 'Shadow Elf Sovereign', faction: 'demon', tier: 'boss',
        hp: 560, attack: 52, defense: 26, speed: 1.1, attackRange: 'melee',
        xpReward: 280, goldReward: 140, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_darkelf', abilities: [],
    },
    {
        id: 'boss_gorgon', name: 'Gorgon Queen', faction: 'orc', tier: 'boss',
        hp: 820, attack: 58, defense: 35, speed: 0.75, attackRange: 'melee',
        xpReward: 300, goldReward: 160, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_gorgon', abilities: [],
    },
    // Echo variant — slightly higher movespeed + attack, and a DoT "echo" special.
    // Visually semi-transparent so she reads as a spectral version of the Queen.
    {
        id: 'boss_gorgon_echo', name: 'Gorgon Echo', faction: 'orc', tier: 'boss',
        hp: 820, attack: 68, defense: 35, speed: 1.00, attackRange: 'melee',
        xpReward: 300, goldReward: 160, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_gorgon2', abilities: [],
    },
    {
        id: 'boss_voidelf', name: 'Void Elf Warlord', faction: 'demon', tier: 'boss',
        hp: 620, attack: 55, defense: 24, speed: 1.2, attackRange: 'melee',
        xpReward: 290, goldReward: 150, lootChance: 1.0, lootTier: 5,
        spriteKey: 'boss_voidelf', abilities: [],
    },
    // Beast boss — near-black recolor of the wolf sprite with Rally Howl that buffs nearby pack.
    // Dark Forest is zone 1 so stats sit at the lower end of the early-boss curve.
    {
        id: 'boss_wolf_lord', name: 'White Claw', faction: 'beast', tier: 'boss',
        hp: 200, attack: 20, defense: 10, speed: 1.2, attackRange: 'melee',
        xpReward: 120, goldReward: 60, lootChance: 1.0, lootTier: 4,
        spriteKey: 'enemy_wolf', abilities: [],
    },
    // ── Satyr — Demon Rift spawnable enemy ──
    {
        id: 'satyr', name: 'Satyr', faction: 'demon', tier: 'minion',
        hp: 55, attack: 17, defense: 7, speed: 1.2, attackRange: 'melee',
        xpReward: 20, goldReward: 11, lootChance: 0.10, lootTier: 1,
        spriteKey: 'enemy_satyr', abilities: [],
    },
    {
        id: 'whelp', name: 'Dragon Whelp', faction: 'dragon', tier: 'minion',
        hp: 45, attack: 15, defense: 8, speed: 1.2, attackRange: 'ranged',
        xpReward: 18, goldReward: 10, lootChance: 0.10, lootTier: 1,
        spriteKey: 'enemy_dragon', abilities: [],
    },
    // ── Minotaur faction — Ashen Labyrinth (level 30+) ──
    {
        id: 'minotaur_grunt', name: 'Minotaur Warrior', faction: 'minotaur', tier: 'minion',
        hp: 90, attack: 22, defense: 12, speed: 1.5, attackRange: 'melee',
        xpReward: 28, goldReward: 16, lootChance: 0.12, lootTier: 3,
        spriteKey: 'enemy_minotaur', abilities: [],
    },
    {
        id: 'minotaur_berserker', name: 'Minotaur Berserker', faction: 'minotaur', tier: 'minion',
        hp: 65, attack: 28, defense: 7, speed: 2.6, attackRange: 'melee',
        xpReward: 26, goldReward: 15, lootChance: 0.11, lootTier: 3,
        spriteKey: 'enemy_minotaur', abilities: [],
    },
    {
        id: 'minotaur_thrower', name: 'Minotaur Axethrower', faction: 'minotaur', tier: 'minion',
        hp: 55, attack: 20, defense: 6, speed: 1.8, attackRange: 'ranged',
        xpReward: 24, goldReward: 14, lootChance: 0.11, lootTier: 3,
        spriteKey: 'enemy_minotaur_ranged', abilities: [],
    },
    {
        id: 'minotaur_champion', name: 'Minotaur Champion', faction: 'minotaur', tier: 'elite',
        hp: 160, attack: 32, defense: 18, speed: 1.4, attackRange: 'melee',
        xpReward: 60, goldReward: 32, lootChance: 0.22, lootTier: 4,
        spriteKey: 'enemy_minotaur_elite', abilities: [],
    },
    {
        id: 'minotaur_warden', name: 'Minotaur Warden', faction: 'minotaur', tier: 'elite',
        hp: 120, attack: 26, defense: 22, speed: 1.3, attackRange: 'melee',
        xpReward: 55, goldReward: 30, lootChance: 0.20, lootTier: 4,
        spriteKey: 'enemy_minotaur_elite', abilities: [],
    },
    {
        id: 'boss_lava_golem', name: 'Magmarath the Undying', faction: 'minotaur', tier: 'boss',
        // +10% base pass after the spell-multiplier nerf — was reading slightly
        // too soft after Inferno Wave / Ground Slam came down. Bumped 1100→1210
        // HP, 72→79 ATK, 40→44 DEF, xp 420→460, gold 200→220. Telurion's then
        // raised separately to keep his +10% premium over Magmarath intact.
        hp: 1210, attack: 79, defense: 44, speed: 0.8, attackRange: 'ranged',
        xpReward: 460, goldReward: 220, lootChance: 1.0, lootTier: 6,
        spriteKey: 'boss_lava_golem', abilities: [],
    },
    // ── Telurion's Lair (event boss, stage 30+). Earth-and-storm dragon with
    //    Stormcall (chain lightning), Verdant Spike Field, and Quake Roar (root).
    //    Same scaling as Magmarath. Faction 'dragon' so vsDragonPct sigil affix
    //    + Dragonheart Shard relic ("+40% damage against dragons") both apply. ──
    {
        id: 'boss_telurion', name: 'Telurion', faction: 'dragon', tier: 'boss',
        // Telurion is the optional event boss with a guaranteed Stormcoil Charm
        // reward. Per user feedback, designed +30 % across HP / ATK / DEF vs
        // the previous tuning (was 1650 / 108 / 60 → now 2145 / 140 / 78) so
        // the encounter has more weight than Magmarath instead of feeling soft.
        // Spell multipliers stay at parity; the bump is purely base stats.
        // attackRange='ranged' + semi-range override (180-280 px band, see
        // BattleScene's atkRange switch) keeps the dragon hovering at mid-
        // distance instead of walking into melee — his Stormcall / Verdant
        // Spike / Quake Roar carry the encounter.
        hp: 2145, attack: 140, defense: 78, speed: 0.7, attackRange: 'ranged',
        xpReward: 780, goldReward: 364, lootChance: 1.0, lootTier: 6,
        spriteKey: 'enemy_telurion', abilities: [],
    },
    // ── Shadowlord (boss_shadowlord) — stage-50 endboss, gates endless mode.
    //    The post-Magmarath fight. Three phases gated on HP%:
    //      Phase 1 (100→75 %): plants fire traps on grid slots, autoattacks
    //      Phase 2 (75→50 %):  becomes invulnerable, summons elites, then breaks
    //      Phase 3 (<50 %):    casts kill-zone spell — be in melee or out at
    //                          range, mid-distance hit takes lethal damage
    //    On death: portal in the arena lights up and endless mode begins
    //    (BattleScene.shadowrealmEndlessActive). attackRange='melee' so he
    //    closes for autoattacks; phase abilities operate at any range.
    //    Stats sit ~40 % above Telurion across HP/ATK/DEF — this is the
    //    final pre-endless wall and should feel meaningfully heavier. ──
    {
        id: 'boss_shadowlord', name: 'Malakhar, the Hollow King', faction: 'void', tier: 'boss',
        hp: 3000, attack: 195, defense: 110, speed: 0.65, attackRange: 'melee',
        xpReward: 1200, goldReward: 520, lootChance: 1.0, lootTier: 6,
        spriteKey: 'enemy_shadowlord', abilities: [],
    },
    // ── Endless ghost (endless_ghost) — sole minion type for the portal-
    //    opens-after-Shadowlord endless run. Demon_1 sheet with a violet
    //    tint + alpha 0.75 in BattleScene gives the spectral look. Stats
    //    are deliberately mid-tier so the threat scales via density (mobs
    //    per second) and the +0.1 % HP/ATK per-kill ramp, not raw values. ──
    {
        id: 'endless_ghost', name: 'Shadowrealm Wraith', faction: 'void', tier: 'minion',
        hp: 90, attack: 24, defense: 6, speed: 1.05, attackRange: 'melee',
        xpReward: 22, goldReward: 9, lootChance: 0.04, lootTier: 1,
        spriteKey: 'enemy_ghost', abilities: [],
    },
    // Golden Skeleton Warrior — Telurion's Lair elite mob. Reuses enemy_skeleton sprite
    // with a runtime gold tint. Same stats as goblin_elite for difficulty parity.
    {
        id: 'skeleton_elite_gold', name: 'Sentinel of Telurion', faction: 'undead', tier: 'elite',
        // Boss-add tier — meaningfully stronger than zone-pool elites since they
        // gate the boss and ALL of them must die before Telurion spawns. Was
        // 80/22/12; bumped ~2.5× HP, ~2.3× ATK so the lair pre-fight is a real
        // pressure phase rather than mooks the party melts in 4 s.
        hp: 200, attack: 50, defense: 28, speed: 1.0, attackRange: 'melee',
        xpReward: 90, goldReward: 48, lootChance: 0.30, lootTier: 4,
        spriteKey: 'enemy_skeleton', abilities: [],
    },
    // ── Rot Bomber — random event mob (stage 10+, ~15% per wave). Not in wave pools; spawned
    //     directly by BattleScene.spawnRotBomber(). Tier tagged 'elite' so HP scales via
    //     ELITE_HP_MULT at scale time if we ever route it through scaleEnemy. The live spawn
    //     uses a bespoke 2.5× base multiplier (between elite 2× and boss 8×). Does not attack
    //     — explodes on death for ~85% of a hero's max HP within 200px. ──
    {
        id: 'rot_bomber', name: 'Rot Bomber', faction: 'demon', tier: 'elite',
        hp: 80, attack: 0, defense: 6, speed: 1.2, attackRange: 'melee',
        xpReward: 60, goldReward: 30, lootChance: 0.25, lootTier: 3,
        spriteKey: 'enemy_rotbomber', abilities: [],
    },
    // ── Endgame: Shadow Realm (level 100+) ──
    {
        id: 'void_worm', name: 'Void Worm', faction: 'void', tier: 'minion',
        hp: 175, attack: 26, defense: 5, speed: 2.0, attackRange: 'ranged',
        xpReward: 40, goldReward: 20, lootChance: 0.10, lootTier: 4,
        spriteKey: 'enemy_void_worm', abilities: [],
    },
    {
        id: 'demoniac_ghost', name: 'Abyss Wraith', faction: 'demon', tier: 'elite',
        hp: 300, attack: 40, defense: 20, speed: 1.1, attackRange: 'ranged',
        xpReward: 150, goldReward: 80, lootChance: 0.30, lootTier: 5,
        spriteKey: 'enemy_wraith', abilities: [],
    },
    {
        id: 'void_walker', name: 'Shadow Demon', faction: 'demon', tier: 'elite',
        hp: 250, attack: 35, defense: 25, speed: 0.9, attackRange: 'melee',
        xpReward: 130, goldReward: 70, lootChance: 0.28, lootTier: 5,
        spriteKey: 'enemy_demon', abilities: [],
    },
    {
        id: 'spectre', name: 'Spectre', faction: 'undead', tier: 'elite',
        hp: 200, attack: 45, defense: 10, speed: 1.3, attackRange: 'ranged',
        xpReward: 140, goldReward: 75, lootChance: 0.28, lootTier: 5,
        spriteKey: 'enemy_wraith', abilities: [],
    },
];
// ---------- Zone Definitions ----------
// Per-biome wave density. Bumped 2026-04-26 (~+50%) so each wave reads as a
// real pack push instead of a thin trickle. Boss-wave count is gated separately
// inside generateWaveEnemies (boss + a smaller add count) so this only thickens
// minion waves 1-4. getLevelBonusEnemies still adds +1 per 10 stages on top.
export const ZONES = [
    { id: 1, name: 'Dark Forest', difficulty: 1, factions: ['beast'], minEnemies: 5, maxEnemies: 8, hasBoss: true, themeKey: 'zone_forest' },
    { id: 2, name: 'Haunted Crypts', difficulty: 1.5, factions: ['undead'], minEnemies: 5, maxEnemies: 8, hasBoss: true, themeKey: 'zone_crypt' },
    { id: 3, name: 'Orcish Wastes', difficulty: 2, factions: ['orc'], minEnemies: 6, maxEnemies: 9, hasBoss: true, themeKey: 'zone_wastes' },
    { id: 4, name: 'Demon Rift', difficulty: 3, factions: ['demon', 'dragon'], minEnemies: 7, maxEnemies: 10, hasBoss: true, themeKey: 'zone_rift' },
    { id: 5, name: 'Shadow Realm', difficulty: 4, factions: ['undead', 'demon', 'void'], minEnemies: 8, maxEnemies: 11, hasBoss: true, themeKey: 'zone_shadow' },
    { id: 6, name: 'Ashen Labyrinth', difficulty: 3.5, factions: ['minotaur'], minEnemies: 7, maxEnemies: 10, hasBoss: true, themeKey: 'zone_labyrinth', minLevel: 38 },
];
/** Zone-specific boss def IDs — picked instead of random elites on boss wave */
const ZONE_BOSS_IDS = {
    'Dark Forest': ['boss_wolf_lord'],
    'Haunted Crypts': ['boss_spearman', 'boss_skeleton_mage'], // skeleton_mage gated by Archive Beneath quest
    'Orcish Wastes': ['boss_gorgon', 'boss_gorgon_echo'],
    'Demon Rift': ['boss_darkelf', 'boss_voidelf'],
    'Shadow Realm': ['boss_demon1', 'boss_demon2'],
    'Ashen Labyrinth': ['boss_lava_golem'],
};
/** Flattened set of every boss def id that can spawn in normal play. Kept for
 *  cross-zone callers (e.g. Inferno mode endgame mixes). Elite Pack now uses
 *  `getZoneBossIds` instead so its rolls stay biome-faithful. */
export function getAllBossDefIds() {
    const all = [];
    for (const ids of Object.values(ZONE_BOSS_IDS))
        all.push(...ids);
    return all;
}
/** Boss def IDs for a single zone, keyed by the zone's display name (with the
 *  " (Tier X)" suffix already stripped). Returns an empty array if the zone
 *  isn't in the boss map. Used by Elite Pack so its roll only picks the
 *  current biome's bosses — keeps the encounter biome-consistent. */
export function getZoneBossIds(zoneName) {
    const baseName = zoneName.replace(/ \(Tier \d+\)$/, '');
    return [...(ZONE_BOSS_IDS[baseName] ?? [])];
}
/**
 * Get zone for a given level. Cycles through available zones with increasing difficulty.
 * Ashen Labyrinth (zone 6) only enters the rotation at level 30+.
 * Levels 100+ get endgame demoniac ghost enemies mixed in.
 */
/**
 * Get zone for an Inferno run zone number (1-based).
 * Unlike getZoneForLevel, this advances one biome per inferno zone so there
 * is no repetition, and all biomes (including Ashen Labyrinth) are always
 * eligible. Difficulty scales ×1.25 per full rotation through all biomes
 * (was ×1.5 — that compounded into an unplayable cliff past zone 30).
 *
 * INFERNO SAVE-TYPE TUNING:
 * - Base difficulty multiplier ×1.35 right out of the gate so inferno-zone-1
 *   reads roughly like a normal stage 5-6 fight (heroes start at lv1, but the
 *   content is already past the early-game softener window).
 * - Per-cycle multiplier kept at ×1.25 (compounds: cycle 2 = base × 1.25,
 *   cycle 3 = base × 1.56, cycle 4 = base × 1.95, ...).
 * - Enemy count `+2` per zone via `minEnemies/maxEnemies` so packs feel denser.
 * - Boss HP/ATK get an additional ×1.20 in BattleScene's spawn path
 *   (`gsm.player.infernoSave === true` branch in spawnEnemy).
 */
export function getZoneForInferno(infernoZone, baseLevel = 39) {
    const zoneIndex = (infernoZone - 1) % ZONES.length;
    const cycle = Math.floor((infernoZone - 1) / ZONES.length);
    const zone = { ...ZONES[zoneIndex] };
    // ×1.35 baseline endgame floor + per-cycle compound. Net at zone 1: ×1.35;
    // at zone 7 (cycle 1): ×1.69; at zone 13 (cycle 2): ×2.11; at zone 19
    // (cycle 3): ×2.64. Tuned for "endgame the player wants" vs "wall".
    zone.difficulty *= 1.35 * Math.pow(1.25, cycle);
    zone.id = baseLevel + infernoZone - 1; // scale from entry level
    zone.name = cycle > 0 ? `${ZONES[zoneIndex].name} (Tier ${cycle + 1})` : ZONES[zoneIndex].name;
    // Denser packs — +2 minimum, +2 maximum so wave-spawn ranges shift up.
    zone.minEnemies += 2;
    zone.maxEnemies += 2;
    return zone;
}
export function getZoneForLevel(level) {
    // Build the eligible zone pool for this level
    // At level 38+, Orcish Wastes leaves the rotation (Ashen Labyrinth takes its place)
    let eligibleZones = ZONES.filter((z) => level >= (z.minLevel ?? 1));
    if (level >= 38)
        eligibleZones = eligibleZones.filter((z) => z.name !== 'Orcish Wastes');
    const pool = eligibleZones.length > 0 ? eligibleZones : ZONES.slice(0, 5);
    const totalZoneIndex = Math.floor((level - 1) / LEVELS_PER_ZONE);
    const zoneIndex = totalZoneIndex % pool.length;
    const cycle = Math.floor(totalZoneIndex / pool.length);
    const zone = { ...pool[zoneIndex] };
    // Each full cycle increases difficulty multiplicatively
    zone.difficulty *= Math.pow(1.5, cycle);
    zone.id = level;
    zone.name = cycle > 0 ? `${pool[zoneIndex].name} (Tier ${cycle + 1})` : pool[zoneIndex].name;
    // Level 100+: add demon faction for demoniac ghosts
    if (level >= 100 && !zone.factions.includes('demon')) {
        zone.factions = [...zone.factions, 'demon'];
    }
    return zone;
}
/**
 * Get enemy speed multiplier for a given zone level.
 * Levels 1-10: 34% movespeed, 67% attack speed (slow early game)
 * Levels 11-49: linearly scale to 100% at level 50
 * Levels 50-99: continue ramping to 130% at level 100
 * Levels 100+: +2% per level above 100 (aggressive late-game scaling)
 */
export function getEnemySpeedMult(zoneLevel) {
    if (zoneLevel <= EARLY_GAME_MAX_LEVEL) {
        return { moveMult: EARLY_GAME_MOVE_SPEED_PENALTY, atkMult: EARLY_GAME_ATK_SPEED_PENALTY };
    }
    if (zoneLevel >= MAX_SPEED_LEVEL) {
        // Level 100 = 1.3x, then +2% per level above 100
        const overLevel = zoneLevel - MAX_SPEED_LEVEL;
        const bonus = 1.3 + overLevel * 0.02;
        return { moveMult: bonus, atkMult: bonus };
    }
    if (zoneLevel <= 50) {
        // Levels 11-50: scale from early penalty to 1.0
        const progress = (zoneLevel - EARLY_GAME_MAX_LEVEL) / (50 - EARLY_GAME_MAX_LEVEL);
        const moveMult = EARLY_GAME_MOVE_SPEED_PENALTY + progress * (1 - EARLY_GAME_MOVE_SPEED_PENALTY);
        const atkMult = EARLY_GAME_ATK_SPEED_PENALTY + progress * (1 - EARLY_GAME_ATK_SPEED_PENALTY);
        return { moveMult, atkMult };
    }
    // Levels 51-99: scale from 1.0 to 1.3
    const progress = (zoneLevel - 50) / (MAX_SPEED_LEVEL - 50);
    const mult = 1 + progress * 0.3;
    return { moveMult: mult, atkMult: mult };
}
/**
 * Scale an enemy definition for a given zone level.
 */
export function scaleEnemy(base, zoneLevel, isBoss) {
    const lvl = zoneLevel;
    // Piecewise HP scaling — stages 1 through ENEMY_HP_EARLY_CAP (=10) compound at the softer
    // ENEMY_HP_PER_LEVEL_EARLY (1.10) so new players aren't walled before they've learned
    // the kit. Past the cap, the normal ENEMY_HP_PER_LEVEL (1.15) resumes from the cap-stage
    // HP as its new baseline so the curve stays continuous (no jump at stage 11).
    const hpScale = lvl <= ENEMY_HP_EARLY_CAP
        ? Math.pow(ENEMY_HP_PER_LEVEL_EARLY, lvl - 1)
        : Math.pow(ENEMY_HP_PER_LEVEL_EARLY, ENEMY_HP_EARLY_CAP - 1)
            * Math.pow(ENEMY_HP_PER_LEVEL, lvl - ENEMY_HP_EARLY_CAP);
    // Global difficulty balance pass: +10% HP baked into all enemies
    let hp = Math.floor(base.hp * hpScale * 1.10);
    let attack = Math.floor(base.attack * Math.pow(ENEMY_ATK_PER_LEVEL, lvl - 1));
    let defense = Math.floor(base.defense * Math.pow(ENEMY_DEF_PER_LEVEL, lvl - 1));
    let goldReward = Math.floor(base.goldReward * Math.pow(ENEMY_GOLD_PER_LEVEL, lvl - 1));
    let xpReward = Math.floor(base.xpReward * Math.pow(ENEMY_XP_PER_LEVEL, lvl - 1));
    let lootChance = base.lootChance;
    if (base.tier === 'elite' && !isBoss) {
        hp = Math.floor(hp * ELITE_HP_MULT);
        attack = Math.floor(attack * ELITE_ATK_MULT);
    }
    // Late-game minion / elite HP ramp — stages 41+ add +1.5 % HP per stage
    // past 40 to non-boss enemies. Bosses are already over-tuned at high
    // stages so they're excluded. Stage 41 = +1.5 %, stage 50 = +15 %,
    // stage 60 = +30 %. Smooths the elite-vs-boss HP gap that opened up
    // once the boss curve pulled away from minion/elite HP at endgame.
    if (!isBoss && zoneLevel > 40) {
        const lateBonus = (zoneLevel - 40) * 0.015;
        hp = Math.floor(hp * (1 + lateBonus));
    }
    if (isBoss) {
        hp = Math.floor(hp * BOSS_HP_MULT);
        attack = Math.floor(attack * BOSS_ATK_MULT);
        defense = Math.floor(defense * BOSS_DEF_MULT);
        goldReward = Math.floor(goldReward * BOSS_GOLD_MULT);
        xpReward = Math.floor(xpReward * BOSS_XP_MULT);
        lootChance = BOSS_LOOT_CHANCE;
    }
    // ── Early-game softener (stages 1–5) ─────────────────────────────────
    // Without a developed talent tree the first five stages were walling
    // fresh runs — every enemy cohort (mob / elite / boss) gets a flat
    // 30% HP + ATK cut for stages 1-5. Stage 6 partial-taper at 85% so the
    // transition isn't a cliff; stage 7+ uses the full curve. DEF is left
    // alone so heroes still feel slight mitigation scaling; HP + ATK are
    // the TTK and survivability levers, which are what actually bite.
    if (zoneLevel <= 5) {
        hp = Math.max(1, Math.floor(hp * 0.70));
        attack = Math.max(1, Math.floor(attack * 0.70));
    }
    else if (zoneLevel === 6) {
        hp = Math.max(1, Math.floor(hp * 0.85));
        attack = Math.max(1, Math.floor(attack * 0.85));
    }
    // Apply level-based speed scaling
    const speedMult = getEnemySpeedMult(zoneLevel);
    return {
        instanceId: `enemy_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        defId: base.id,
        name: base.name,
        faction: base.faction,
        tier: isBoss ? 'boss' : base.tier,
        currentHp: hp,
        maxHp: hp,
        attack,
        defense,
        speed: base.speed * speedMult.moveMult * (isBoss ? 1.3 : 1) * 1.20, // +20% global movespeed bump + bosses 30% faster
        attackSpeedMult: speedMult.atkMult,
        attackRange: base.attackRange,
        position: { x: 0, y: 0 },
        xpReward,
        goldReward,
        lootChance,
        lootTier: isBoss ? base.lootTier + 2 : base.lootTier,
        debuffs: [],
    };
}
/**
 * Extra enemies added to waves based on zone level.
 * +1 enemy every 10 levels, up to +8 extra at level 80+.
 */
function getLevelBonusEnemies(zoneLevel) {
    return Math.min(8, Math.floor(zoneLevel / 10));
}
/**
 * Elite spawn chance scales with zone level and wave number.
 * Base 15% + 2% per wave + 0.5% per zone level (caps at 70%).
 */
function getEliteChance(zoneLevel, waveNumber) {
    return Math.min(0.70, 0.15 + waveNumber * 0.02 + zoneLevel * 0.005);
}
/**
 * Generate enemies for a specific wave in a zone.
 */
export function generateWaveEnemies(zoneLevel, waveNumber, zoneOverride, 
/** Boss def IDs the player hasn't unlocked yet (e.g. story-quest-gated bosses) —
 *  filtered out of the zone boss pool so they can't spawn before the quest opens. */
lockedBossIds = new Set(), 
/** A specific boss def ID that MUST spawn on this boss wave (story-quest active-beat
 *  targeting). Takes precedence over the random pool. Ignored if not a boss wave. */
forcedBossId) {
    const zone = zoneOverride ?? getZoneForLevel(zoneLevel);
    const isBossWave = waveNumber === WAVES_PER_ZONE;
    // Get eligible enemies for this zone's factions. Event-only mobs are
    // excluded so they only appear in their dedicated event spawns:
    //   • `skeleton_elite_gold` — Telurion's Lair Sentinel; spawned in
    //     startTelurionsLair (BASE_ENEMIES.find) so the Telurion story-quest
    //     beat doesn't tick down from skeletons killed in the Haunted Crypts.
    //   • `rot_bomber` — random Demon Rift event mob; spawned directly by
    //     BattleScene.spawnRotBomber. Without this exclusion the rot-bomber
    //     sprite leaked into normal Demon Rift waves (faction 'demon' put it
    //     in the eligible pool — user-reported "saw it in demon rift" bug).
    const EVENT_ONLY_IDS = new Set(['skeleton_elite_gold', 'rot_bomber']);
    let eligible = BASE_ENEMIES.filter((e) => zone.factions.includes(e.faction) && !EVENT_ONLY_IDS.has(e.id));
    // Level 50+: add endgame enemies to the pool — but ONLY if their faction
    // matches the current zone's factions. Previously this injected demon
    // (demoniac_ghost / void_walker) + undead (spectre) into EVERY biome at
    // lvl 50, leaking demon ghosts into Dark Forest and undead spectres
    // into Orcish Wastes — the user-reported "mixed up enemies in some
    // biomes" bug. Now Dark Forest stays beasts, Crypts gets spectres,
    // Demon Rift / Shadow Realm get the demon variants, etc.
    if (zoneLevel >= 50) {
        for (const id of ['demoniac_ghost', 'void_walker', 'spectre']) {
            const endgame = BASE_ENEMIES.find((e) => e.id === id);
            if (endgame && zone.factions.includes(endgame.faction) && !eligible.includes(endgame)) {
                eligible = [...eligible, endgame];
            }
        }
    }
    if (eligible.length === 0)
        return [];
    const enemies = [];
    const bonusEnemies = getLevelBonusEnemies(zoneLevel);
    if (isBossWave) {
        // Use zone-specific named boss if available, otherwise fall back to random elite.
        // lockedBossIds strips quest-gated bosses from the random pool (e.g. Skeleton Mage is
        // hidden until the Archive Beneath quest's first two beats are done); forcedBossId
        // bypasses the random pick entirely when the active story beat targets a specific boss.
        const baseName = zone.name.replace(/ \(Tier \d+\)$/, ''); // strip " (Tier X)" suffix
        const zoneBossIds = (ZONE_BOSS_IDS[baseName] ?? []).filter((id) => !lockedBossIds.has(id));
        let bossBase;
        if (forcedBossId) {
            bossBase = BASE_ENEMIES.find((e) => e.id === forcedBossId) ?? eligible[0];
        }
        else if (zoneBossIds.length > 0) {
            const bossId = zoneBossIds[Math.floor(Math.random() * zoneBossIds.length)];
            bossBase = BASE_ENEMIES.find((e) => e.id === bossId) ?? eligible[0];
        }
        else {
            const elites = eligible.filter((e) => e.tier === 'elite' || e.tier === 'boss');
            bossBase = elites.length > 0 ? elites[Math.floor(Math.random() * elites.length)] : eligible[0];
        }
        enemies.push(scaleEnemy(bossBase, zoneLevel, true));
        // Boss escorts scale with level: base 2 + level/3 + bonus
        const escortCount = 2 + Math.floor(zoneLevel / 3) + Math.floor(bonusEnemies / 2);
        const minions = eligible.filter((e) => e.tier === 'minion');
        const escortElites = eligible.filter((e) => e.tier === 'elite');
        for (let i = 0; i < escortCount; i++) {
            // Higher levels mix in elite escorts
            const useElite = escortElites.length > 0 && Math.random() < getEliteChance(zoneLevel, waveNumber);
            const pool = useElite ? escortElites : minions;
            if (pool.length > 0) {
                enemies.push(scaleEnemy(pool[Math.floor(Math.random() * pool.length)], zoneLevel, false));
            }
        }
    }
    else {
        const nonBossEligible = eligible.filter((e) => e.tier !== 'boss');
        const baseCount = zone.minEnemies + Math.floor(Math.random() * (zone.maxEnemies - zone.minEnemies + 1));
        const count = baseCount + bonusEnemies;
        const eliteChance = getEliteChance(zoneLevel, waveNumber);
        for (let i = 0; i < count; i++) {
            const useElite = Math.random() < eliteChance;
            const pool = useElite
                ? nonBossEligible.filter((e) => e.tier === 'elite')
                : nonBossEligible.filter((e) => e.tier === 'minion');
            const pick = pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : nonBossEligible[0] ?? eligible[0];
            enemies.push(scaleEnemy(pick, zoneLevel, false));
        }
    }
    return enemies;
}
//# sourceMappingURL=waves.js.map